import { track } from '@/flow/instrument';
import { chooseLocale } from './persistLocale';
import { NATIVE_NAMES, SHORT_NAMES, useT } from './runtime';

/**
 * Forked verbatim from vCIP_droom_4.0/src/i18n/LocaleSwitcher.tsx: one visible tap, no dropdown. Round 2: a tap goes
 * through `chooseLocale`, so a mid-session switch re-renders every visible string AND is persisted on the case.
 *
 * `compact` (the camera screens' bottom bar, FSD-20 Addendum C): the same buttons with each language's short own-script
 * label ("EN", "हिं"); the full native name stays the accessible name, so a screen reader still says "हिन्दी".
 */
export function LocaleSwitcher({ compact = false }: { compact?: boolean }): JSX.Element | null {
  // VC-12: the case's offer (pack-gated languages included only when the pack names them)
  const { locale, setLocale, offered: locales } = useT();
  // §10 "Language switch mid-session": counted only when the tap actually changes the language — re-pressing the
  // row you are already on is not a switch.
  const switchTo = (code: string): void => {
    if (code !== locale) track('lang.switch', { from: locale, to: code });
    void chooseLocale(code, setLocale);
  };
  if (locales.length < 2) return null;
  return (
    <div className={compact ? 'locale-switcher is-compact' : 'locale-switcher'} role="group" aria-label="Language / भाषा" data-testid={compact ? 'locale-switcher-compact' : undefined}>
      {locales.map((code) => (
        <button
          key={code}
          type="button"
          lang={code}
          className="locale-choice"
          aria-pressed={locale === code}
          // WCAG 2.5.3 (label in name; VC-12's axe gate): the visible short label FIRST, then the full native name for a
          // screen reader, so a voice-control user who says "EN" still reaches the button (axe drops a parenthetical)
          aria-label={compact ? `${SHORT_NAMES[code] ?? code.toUpperCase()}, ${NATIVE_NAMES[code] ?? code}` : undefined}
          onClick={() => switchTo(code)}
        >
          {compact ? (SHORT_NAMES[code] ?? code.toUpperCase()) : (NATIVE_NAMES[code] ?? code)}
        </button>
      ))}
    </div>
  );
}
